import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { getOrganizationMedicalProfileFolderIdCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationMedicalProfileFolderId', {
    folderLoadFailure: 'Failed to load client folder',
});

export type UseOrganizationMedicalProfileFolderIdArgs = {
    clientProfileId: string | null;
};

export type UseOrganizationMedicalProfileFolderIdReturnValue = {
    folderId: string | null;
    isLoadingFolderId: boolean;
};

export default function useOrganizationMedicalProfileFolderId({
    clientProfileId,
}: UseOrganizationMedicalProfileFolderIdArgs): UseOrganizationMedicalProfileFolderIdReturnValue {
    const organizationId = useOrganizationId();
    const organizationUsersQuery = useQuery({
        queryKey: getOrganizationMedicalProfileFolderIdCacheKey(clientProfileId || '*'),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationMedicalProfileFolderIdResponse>(
                urls.api.organizations.files.organizationMedicalProfileFileFolder(organizationId, clientProfileId!),
            );
        },
        enabled: !!clientProfileId,
    });
    useQueryErrorNotify(organizationUsersQuery, intl('folderLoadFailure'));

    return {
        folderId: organizationUsersQuery.data?.medicalProfileFileFolderId || null,
        isLoadingFolderId: organizationUsersQuery.isLoading,
    };
}
